import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { verifyEmail, resendVerification } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useOrgStore } from '../store/orgStore';
import { AUTO_ACCEPT_REASON_MESSAGES } from '../constants/invitationStatusLabels';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const { setActiveOrgId, fetchOrganizations } = useOrgStore();

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const invitationToken = searchParams.get('invitationToken');

  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // 토큰이 있으면 자동 인증 처리
  useEffect(() => {
    if (!token) return;

    setVerifying(true);
    verifyEmail(token)
      .then(async (result) => {
        setVerified(true);
        setAuth(result.user, result.tokens.accessToken, result.tokens.refreshToken);

        // CHG-141 REQ-IF-010: 자동 합류 결과 처리
        const autoAccept = result.autoAccept;
        let redirectTo = '/onboarding';

        if (autoAccept?.status === 'accepted') {
          if (autoAccept.organizationId) {
            await fetchOrganizations();
            setActiveOrgId(autoAccept.organizationId);
            toast.success(`조직 ${autoAccept.organizationName ?? ''}에 합류했습니다!`);
            redirectTo = '/';
          } else if (autoAccept.projectId) {
            toast.success('프로젝트에 합류했습니다!');
            redirectTo = `/projects/${autoAccept.projectKey ?? autoAccept.projectId}`;
          }
        } else if (autoAccept?.status === 'skipped' && autoAccept.reason) {
          const msg = AUTO_ACCEPT_REASON_MESSAGES[autoAccept.reason] ?? '자동 합류에 실패했습니다.';
          toast(msg, { icon: 'ℹ️' });
          if (autoAccept.reason === 'already_member' && autoAccept.organizationId) {
            await fetchOrganizations();
            setActiveOrgId(autoAccept.organizationId);
            redirectTo = '/';
          } else {
            redirectTo = '/profile';
          }
        } else if (invitationToken) {
          // 자동 합류 응답 없으나 invitationToken이 query에 있는 경우 — 수락 페이지로 이동
          redirectTo = `/org-invitations/${invitationToken}/accept`;
        } else {
          toast.success('이메일 인증이 완료되었습니다!');
        }
        setTimeout(() => navigate(redirectTo, { replace: true }), 1200);
      })
      .catch((err) => {
        const errData = (err as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
        // CHG-141 EDGE-007: 토큰 재사용 → 명확한 안내 + 로그인 CTA
        const msg = errData?.message ?? '인증에 실패했습니다.';
        setError(msg);
      })
      .finally(() => setVerifying(false));
  }, [token, setAuth, navigate, fetchOrganizations, setActiveOrgId, invitationToken]);

  // 쿨다운 카운터
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = useCallback(async () => {
    if (!email || cooldown > 0) return;
    try {
      // CHG-141 EDGE-006: 응답의 invitationCarriedOver/statusOnResend를 토스트로 안내
      const res = await resendVerification(email);
      const data = res.data;
      if (data && data.invitationCarriedOver === false && data.invitationStatusOnResend) {
        toast(`인증 메일을 재발송했습니다. 초대는 ${data.invitationStatusOnResend} 상태라 일반 가입으로 진행됩니다.`, { icon: 'ℹ️' });
      } else {
        toast.success('인증 메일을 재발송했습니다.');
      }
      setCooldown(60);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '메일 발송에 실패했습니다.';
      toast.error(msg);
    }
  }, [email, cooldown]);

  // 토큰 인증 진행 중
  if (token && verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <Loader2 className="size-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">이메일 인증 중...</p>
        </div>
      </div>
    );
  }

  // 토큰 인증 완료
  if (token && verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <CheckCircle className="size-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">인증 완료!</h2>
          <p className="text-gray-500">잠시 후 온보딩 화면으로 이동합니다...</p>
        </div>
      </div>
    );
  }

  // 토큰 인증 실패
  if (token && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="size-6 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">인증 실패</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link
            to="/register"
            className="text-primary-600 font-medium hover:text-primary-700"
          >
            다시 가입하기
          </Link>
        </div>
      </div>
    );
  }

  // 인증 대기 화면 (토큰 없음, 이메일만 있음)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600">MaioS</h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="size-8 text-primary-600" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">이메일 인증 안내</h2>
          <p className="text-gray-500 mb-2">
            {email ? (
              <>
                <strong className="text-gray-700">{email}</strong>로
              </>
            ) : (
              '입력하신 이메일로'
            )}
          </p>
          <p className="text-gray-500 mb-6">인증 메일을 발송했습니다. 메일함을 확인해주세요.</p>

          <p className="text-sm text-gray-400 mb-4">
            메일이 도착하지 않았나요? 스팸 폴더를 확인하거나 재발송해주세요.
          </p>

          {email && (
            <button
              onClick={handleResend}
              disabled={cooldown > 0}
              className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {cooldown > 0 ? `재발송 가능 (${cooldown}초)` : '인증 메일 재발송'}
            </button>
          )}

          <p className="mt-6 text-sm text-gray-500">
            <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700">
              로그인으로 돌아가기
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
